import React, { useState, useEffect, lazy, Suspense } from "react";
import {
  Box,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Typography,
  Button,
  Snackbar,
  Tab,
  Tabs,
  FormControl,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ShareIcon from '@mui/icons-material/Share';
import { useAuth } from "../AuthContext";
import WhatsAppIcon from "@mui/icons-material/WhatsApp"; // Importer l'icône WhatsApp
import * as XLSX from "xlsx";
import useAuthenticatedFetch from '../hooks/useAuthenticatedFetch';
import { useTranslation } from "react-i18next";
import { useMediaQuery } from '@mui/material';
import { useTheme } from "@mui/material/styles";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CircularProgress from '@mui/material/CircularProgress';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import { ConfirmDialog } from '../components/dialogs/ConfirmDialog';
import SectionExportImage from '../utils/SectionExportImage';
import { updateVariableTimes } from '../utils/scheduleUtils';

// Lazy load de l'éditeur
const TinyEditor = lazy(() => import('@tinymce/tinymce-react').then(module => ({
  default: module.Editor
})));

const ChaguimEditor = ({ onIsDirtyChange }) => {
  const { user } = useAuth(); // Utilisateur connecté
  const { t } = useTranslation(); // Pour accéder à i18n
  const [rooms, setRooms] = useState([]);
  const [tefilot, setTefilot] = useState({});
  const [community, setCommunity] = useState([]);
  const [errors, setErrors] = useState([]); // Gestion des erreurs
  const [warning, setWarning] = useState(''); // Gestion des erreurs
  const [successMessage, setSuccessMessage] = useState(''); // Pour le message de confirmation
  const [errorMessage, setErrorMessage] = useState(''); // Pour le message de confirmation
  const fetchWithAuth = useAuthenticatedFetch();
  const { i18n } = useTranslation();
  // Définir les catégories de fêtes
  const chaguimCategories = [
    'Rosh Chodesh',
    'Pessach',
    'Shavouot',
    'Soukot',
    'Rosh Ashana',
    'Kippur',
    'Taanit BAAB'
  ];
  const language = i18n.language; // Obtenir la langue active
  const [activeTab, setActiveTab] = useState(chaguimCategories[0]); // Onglet actif par défaut
  const [loading, setLoading] = useState(true); // Indicateur de chargement
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md")); // Vérifie si l'écran est petit
  const [isDirty, setIsDirty] = useState(false);
  const [information, setInformation] = useState('');
  const [isInformationAccordionOpen, setIsInformationAccordionOpen] = useState(false);
  const [isLoadingInfo, setIsLoadingInfo] = useState(false);
  const [selectedChag, setSelectedChag] = useState('');
  const [sections, setSections] = useState([]);
  const [newSectionDialog, setNewSectionDialog] = useState(false);
  const [newSectionName, setNewSectionName] = useState('');
  const [editingSectionId, setEditingSectionId] = useState(null);
  const [editedSectionName, setEditedSectionName] = useState('');
  const [fontSize, setFontSize] = useState("16");
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({ open: false, sectionId: null });
  const [exportingSection, setExportingSection] = useState(null);
  const [exportImageBlob, setExportImageBlob] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [zmanim, setZmanim] = useState(null);
  const [parasha, setParasha] = useState(null);

  useEffect(() => {
    if (onIsDirtyChange) {
      onIsDirtyChange(isDirty); // Notifier le parent chaque fois que isDirty change
    }
  }, [isDirty, onIsDirtyChange]);

  // Chargement des prières, salles et données existantes
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        // Charger les informations de la communauté
        const communityResponse = await fetchWithAuth(
          `${process.env.REACT_APP_BASE_URL}/community/getCommunityById/${user.communityId}`
        );
        if (!communityResponse.ok) throw new Error(t("schedule.errors.loadCommunity"));
        const communityData = await communityResponse.json();
        setCommunity(communityData);

        // Charger les salles
        const roomsResponse = await fetchWithAuth(
          `${process.env.REACT_APP_BASE_URL}/room/getRooms?communityId=${user.communityId}`
        );
        if (!roomsResponse.ok) throw new Error(t("schedule.errors.loadRooms"));
        const roomsData = await roomsResponse.json();
        setRooms(roomsData);
        // Charger les sections et tefilot
        await loadSectionsAndTefilot();
        
        // Charger les zmanim et parasha pour le traitement des horaires dynamiques
        await loadZmanimAndParasha();

      } catch (error) {
        console.error(error.message);
      } finally {
        setLoading(false); // Terminer le chargement
      }
    };

    fetchData();
  }, [user.communityId, language, t, activeTab]);

  // Charger la taille de police initiale
  useEffect(() => {
    if (community?.fontSizeSetting) {
      const settings = JSON.parse(community.fontSizeSetting);
      const holidaySetting = settings.find(setting => setting.name === 'Holiday');
      if (holidaySetting) {
        setFontSize(holidaySetting.size);
      }
    }
  }, [community]);

  // Charger les informations existantes
  useEffect(() => {
    const fetchInformation = async () => {
      if (!isInformationAccordionOpen) return; // Ne pas charger si l'accordéon est fermé
      
      setIsLoadingInfo(true);
      try {
        const response = await fetchWithAuth(
          `${process.env.REACT_APP_BASE_URL}/tefilot/getHolidayInformation?category=${activeTab}&communityId=${user.communityId}`
        );
        if (response.ok) {
          const data = await response.json();
          setInformation(data.information || '');
        }
      } catch (error) {
        console.error('Error fetching information:', error);
      } finally {
        setIsLoadingInfo(false);
      }
    };

    fetchInformation();
  }, [activeTab, user.communityId, isInformationAccordionOpen]);

  // Charger la valeur initiale de selectedChag
  useEffect(() => {
    if (community?.selectedChag !== undefined) {
      setSelectedChag(community.selectedChag || '');
    }
  }, [community]);

  // Recharger les zmanim quand la communauté change
  useEffect(() => {
    if (community) {
      loadZmanimAndParasha();
    }
  }, [community]);

  const loadSectionsAndTefilot = async () => {
    try {
      // Charger les sections pour l'onglet actif
      const sectionsResponse = await fetchWithAuth(
        `${process.env.REACT_APP_BASE_URL}/tefilot/getPrayerSection?communityId=${user.communityId}&category=${activeTab}`
      );
      if (!sectionsResponse.ok) throw new Error(t("schedule.errors.loadSections"));
      const sectionsData = await sectionsResponse.json();
      setSections(sectionsData);

      // Charger les tefilot
      const tefilotResponse = await fetchWithAuth(
        `${process.env.REACT_APP_BASE_URL}/tefilot/getTefilotCommunities?communityId=${user.communityId}`
      );
      if (!tefilotResponse.ok) throw new Error(t("schedule.errors.loadTefilot"));
      const tefilotData = await tefilotResponse.json();
      console.log('tefilotData', tefilotData);
      console.log('sectionsData', sectionsData);
      // Organiser les tefilot par section
      const tefilotBySection = tefilotData.reduce((acc, tefilah) => {
        if (!acc[tefilah.sectionId]) {
          acc[tefilah.sectionId] = [];
        }
        acc[tefilah.sectionId].push(tefilah);
        return acc;
      }, {});
      
      setTefilot(tefilotBySection);
    } catch (error) {
      console.error(error);
      setErrorMessage(t("schedule.errors.loadData"));
    }
  };

  // Gestion du changement d'onglet
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    // Replier l'accordéon lors du changement d'onglet
    setIsInformationAccordionOpen(false);
  };

  const loadZmanimAndParasha = async () => {
    try {
      if (!community?.latitude || !community?.longitude || !community?.timezone) {
        console.warn("Coordonnées de la communauté non disponibles pour les zmanim");
        return;
      }

      const now = new Date();
      const currentDate = now.toLocaleDateString('en-CA');

      // Charger les zmanim
      const zmanimResponse = await fetch(
        `${process.env.REACT_APP_BASE_URL}/zmanim/getZmanim?latitude=${community.latitude}&longitude=${community.longitude}&timezone=${community.timezone}&date=${currentDate}&communityName=${community.nameForUrl}`
      );
      if (zmanimResponse.ok) {
        const zmanimData = await zmanimResponse.json();
        setZmanim(zmanimData);
      }

      // Charger la parasha
      const parashaResponse = await fetch(
        `${process.env.REACT_APP_BASE_URL}/zmanim/getParasha?language=${community.displayLanguage}&latitude=${community.latitude}&longitude=${community.longitude}&timezone=${community.timezone}&communityName=${community.nameForUrl}`
      );
      if (parashaResponse.ok) {
        const parashaData = await parashaResponse.json();
        setParasha(parashaData);
      }
    } catch (error) {
      console.error("Erreur lors du chargement des zmanim/parasha:", error);
    }
  };

  const handleSave = async () => {
    // ChaguimEditor must only save Chaguim categories (not Week/Shabbat).
    const flattenedSchedule = Object.values(tefilot)
      .flat()
      // Some newly added rows may miss category; default to activeTab.
      .map((row) => ({ ...row, category: row?.category || activeTab }))
      .filter((row) => chaguimCategories.includes(row?.category));
    console.log('flattenedSchedule', flattenedSchedule);

    const validationErrors = flattenedSchedule.map((row) => ({
      sectionId: !row.sectionId,
      name: !row.name,
      zman: row.isVariable && !row.zman,
    }));

    const hasErrors = validationErrors.some((row) => row.sectionId || row.name);
    setErrors(validationErrors);

    if (hasErrors) {
      setErrorMessage(t("common.validationError"));
      return;
    }

    try {
      // Sauvegarder la taille de police en utilisant les données existantes
      const currentSettings = community.fontSizeSetting ? JSON.parse(community.fontSizeSetting) : [];
      const updatedSettings = currentSettings.filter(setting => setting.name !== 'Holiday');
      updatedSettings.push({ name: 'Holiday', size: fontSize });

      await fetchWithAuth(
        `${process.env.REACT_APP_BASE_URL}/community/updateFontSizeSettings`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            communityId: user.communityId,
            fontSizeSetting: JSON.stringify(updatedSettings),
          }),
        }
      );

      // Sauvegarder les horaires
      const scheduleResponse = await fetchWithAuth(
        `${process.env.REACT_APP_BASE_URL}/tefilot/saveTefilotCommunities`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            communityId: user.communityId,
            rows: flattenedSchedule.map(row => ({
              ...row,
              communityId: user.communityId,
              time: row.isVariable ? "00:00" : (row.time || "00:00")
            })),
            source: 'chaguim'
          }),
        }
      );

      // Ne sauvegarder les informations que si l'accordéon a été ouvert
      if (isInformationAccordionOpen) {
        const informationResponse = await fetchWithAuth(
          `${process.env.REACT_APP_BASE_URL}/tefilot/saveHolidayInformation`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              communityId: user.communityId,
              category: activeTab,
              information,
            }),
          }
        );

        if (!informationResponse.ok) {
          throw new Error(t("schedule.errors.serverError"));
        }
      }

      // Sauvegarder le chag sélectionné
      const selectedChagResponse = await fetchWithAuth(
        `${process.env.REACT_APP_BASE_URL}/community/updateSelectedChag`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            communityId: user.communityId,
            selectedChag,
          }),
        }
      );

      if (!scheduleResponse.ok || !selectedChagResponse.ok) {
        throw new Error(t("schedule.errors.serverError"));
      }

      const savePayload = await scheduleResponse.json().catch(() => ({}));
      if (savePayload.partialSave) {
        await loadSectionsAndTefilot();
        setErrorMessage(t("schedule.success.partialSave", { max: savePayload.truncated?.[0]?.max ?? 2 }));
        setTimeout(() => setErrorMessage(''), 8000);
      } else {
        setSuccessMessage(t("schedule.success.save"));
        setTimeout(() => setSuccessMessage(''), 3000);
      }
      setIsDirty(false);
    } catch (error) {
      const code = error?.code || error?.details?.code;
      if (code === "PLAN_LIMIT") {
        setErrorMessage(t("schedule.errors.planLimitPro"));
      } else if (code === "FEATURE_PRO_ONLY") {
        setErrorMessage(t("schedule.errors.proOnly"));
      } else {
        console.error(t("schedule.errors.networkError"), error);
        setErrorMessage(t("schedule.errors.networkError"));
      }
      setTimeout(() => setErrorMessage(''), 3000);
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const newTefilot = { ...tefilot };
    const items = Array.from(newTefilot[activeTab] || []);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setTefilot({
      ...tefilot,
      [activeTab]: items
    });
    setIsDirty(true);
  };

  const handleAddSection = async () => {
    if (!newSectionName.trim()) return;

    try {
      const response = await fetchWithAuth(
        `${process.env.REACT_APP_BASE_URL}/tefilot/savePrayerSection`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            communityId: user.communityId,
            sectionName: newSectionName,
            category: activeTab,
            rank: sections.length
          })
        }
      );

      if (!response.ok) {
        // fetchWithAuth throws on non-OK, but keep defensive behavior in case a different client is used later.
        throw new Error(t("schedule.errors.saveSection"));
      }
      
      const newSection = await response.json();
      setSections([...sections, newSection]);
      setNewSectionName('');
      setNewSectionDialog(false);
      setSuccessMessage(t("schedule.success.addSection"));
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error(error);
      const code = error?.code || error?.details?.code;
      if (code === "PLAN_LIMIT") {
        setErrorMessage(t("schedule.errors.planLimitPro"));
      } else if (code === "FEATURE_PRO_ONLY") {
        setErrorMessage(t("schedule.errors.proOnly"));
      } else {
        setErrorMessage(t("schedule.errors.saveSection"));
      }
    }
  };

  const handleDeleteSection = async (sectionId) => {
    setDeleteConfirmDialog({ open: true, sectionId });
  };

  const handleConfirmDelete = async () => {
    try {
      const response = await fetchWithAuth(
        `${process.env.REACT_APP_BASE_URL}/tefilot/deletePrayerSection/${deleteConfirmDialog.sectionId}?communityId=${user.communityId}`,
        {
          method: 'DELETE'
        }
      );

      if (!response.ok) throw new Error(t("schedule.errors.deleteSection"));

      setSections(sections.filter(section => section.sectionId !== deleteConfirmDialog.sectionId));
      setSuccessMessage(t("schedule.success.deleteSection"));
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error(error);
      setErrorMessage(t("schedule.errors.deleteSection"));
    } finally {
      setDeleteConfirmDialog({ open: false, sectionId: null });
    }
  };

  const handleUpdateSectionName = async (sectionId, newName) => {
    try {
      const response = await fetchWithAuth(
        `${process.env.REACT_APP_BASE_URL}/tefilot/updatePrayerSection`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sectionId,
            sectionName: newName,
          })
        }
      );

      if (!response.ok) throw new Error(t("schedule.errors.updateSection"));

      setSections(sections.map(section => 
        section.sectionId === sectionId 
          ? { ...section, sectionName: newName }
          : section
      ));
      setSuccessMessage(t("schedule.success.updateSection"));
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error(error);
      setErrorMessage(t("schedule.errors.updateSection"));
    }
  };

  const handleAddTefilah = (sectionId) => {
    const newTefilot = {
      ...tefilot,
      [sectionId]: [
        ...(tefilot[sectionId] || []),
        {
          sectionId,
          category: activeTab,
          name: "",
          roomId: "",
          time: "",
          isVariable: false,
          zman: "",
          offSetValue: 0,
          startDate: "",
          endDate: "",
        }
      ]
    };
    setTefilot(newTefilot);
    setIsDirty(true);
  };

  const handleUpdateTefilah = (sectionId, index, field, value) => {
    const updatedTefilot = {
      ...tefilot,
      [sectionId]: tefilot[sectionId].map((t, i) => 
        i === index ? { ...t, [field]: value } : t
      )
    };
    setTefilot(updatedTefilot);
    setIsDirty(true);
  };

  const handleDeleteTefilah = (sectionId, index) => {
    const updatedTefilot = {
      ...tefilot,
      [sectionId]: tefilot[sectionId].filter((_, i) => i !== index)
    };
    setTefilot(updatedTefilot);
    setIsDirty(true);
  };

  // Compter le nombre de tefilot par catégorie
  const getTefilotCount = (category) => {
    return Object.values(tefilot)
      .flat()
      .filter(t => t.category === category)
      .length;
  };

  const handleStartEditing = (section) => {
    setEditingSectionId(section.sectionId);
    setEditedSectionName(section.sectionName);
  };

  const handleSaveSection = async (sectionId) => {
    await handleUpdateSectionName(sectionId, editedSectionName);
    setEditingSectionId(null);
  };

  const handleExportSection = (section) => {
    if (isExporting) return; // Éviter les appels multiples
    
    setIsExporting(true);
    
    // Récupérer les tefilot de la section
    const sectionTefilot = tefilot[section.sectionId] || [];
    
    // Traiter les horaires dynamiques
    const processedTefilot = updateVariableTimes(sectionTefilot, zmanim, parasha, language, community);
    
    // Formater les données pour l'export
    const exportData = processedTefilot.map(tefilah => ({
      name: tefilah.name,
      roomName: rooms.find(room => room.roomId === tefilah.roomId)?.roomName || '',
      time: tefilah.time || ''
    }));
    
    setExportingSection({
      ...section,
      processedData: exportData
    });
  };

  const handleImageGenerated = (blob) => {
    setExportImageBlob(blob);
    const section = exportingSection;
    setExportingSection(null);
    setIsExporting(false); // Réinitialiser le flag
    
    // Créer un lien de téléchargement
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${section.sectionName}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    setSuccessMessage(t("schedule.success.exportImage"));
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  if (loading) {
    return <Typography>{t("common.loading")}</Typography>; // Affiche un indicateur de chargement
  }

  return (
    <Box sx={{ mt: 1, width: '100%' }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        {/* Titre aligné à gauche */}
        <Typography variant="h4" gutterBottom>
          {t("schedule.titleChaguim")}
        </Typography>

        {/* Picklist et taille de police alignés à droite */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            label={t("schedule.fontSize")}
            type="number"
            value={fontSize}
            onChange={(e) => {
              setFontSize(e.target.value);
              setIsDirty(true);
            }}
            size="small"
            sx={{ width: '120px' }}
            InputProps={{
              endAdornment: <span style={{ marginLeft: '4px' }}>px</span>
            }}
          />
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="active-chag-label">{t("schedule.activeChag")}</InputLabel>
            <Select
              labelId="active-chag-label"
              value={selectedChag}
              onChange={(e) => {
                setSelectedChag(e.target.value);
                setIsDirty(true);
              }}
              label={t("schedule.activeChag")}
            >
              <MenuItem value="">{t("schedule.noActiveChag")}</MenuItem>
              {chaguimCategories.map((category) => (
                <MenuItem key={category} value={category}>
                  {t(`schedule.category.${category}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>
      {/* Onglets pour les catégories */}
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        centered={!isSmallScreen}
        variant={isSmallScreen ? "scrollable" : "standard"}
        scrollButtons={isSmallScreen ? "auto" : false}
        allowScrollButtonsMobile
      >
        {chaguimCategories.map((category) => (
          <Tab
            key={category}
            label={`${t(`schedule.category.${category}`)} (${getTefilotCount(category)})`}
            value={category}
          />
        ))}
      </Tabs>
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', mt: 2 }}>
        {/* Premier Accordion pour les horaires */}
        <Accordion defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="horaires-content"
            id="horaires-header"
          >
            <Typography variant="h6">{t("schedule.scheduleSection")}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {/* Bouton pour ajouter une section */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-start', alignItems: 'center', mb: 2 }}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setNewSectionDialog(true)}
              >
                {t("schedule.addSection")}
              </Button>
            </Box>

            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="sections">
                {(provided) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    style={{ minHeight: '100px' }}
                  >
                    {sections.map((section, index) => (
                      <Draggable
                        key={section.sectionId}
                        draggableId={String(section.sectionId)}
                        index={index}
                      >
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            style={{
                              ...provided.draggableProps.style,
                              marginBottom: '8px'
                            }}
                          >
                            <Accordion>
                              <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                              >
                                <Box sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  width: '100%',
                                  gap: 2 
                                }}>
                                  <div {...provided.dragHandleProps}>
                                    <DragIndicatorIcon />
                                  </div>
                                  {editingSectionId === section.sectionId ? (
                                    <>
                                      <TextField
                                        value={editedSectionName}
                                        onChange={(e) => setEditedSectionName(e.target.value)}
                                        onClick={(e) => e.stopPropagation()}
                                        onFocus={(e) => e.stopPropagation()}
                                        variant="standard"
                                        sx={{ minWidth: 150 }}
                                      />
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleSaveSection(section.sectionId);
                                        }}
                                        color="primary"
                                      >
                                        <CheckIcon />
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteSection(section.sectionId);
                                        }}
                                        color="error"
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setEditingSectionId(null);
                                        }}
                                        color="default"
                                      >
                                        <CancelIcon />
                                      </IconButton>
                                    </>
                                  ) : (
                                    <>
                                      <Typography>{section.sectionName}</Typography>
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleStartEditing(section);
                                        }}
                                      >
                                        <EditIcon />
                                      </IconButton>
                                    </>
                                  )}
                                  <Box sx={{ flexGrow: 1 }} />
                                  <Tooltip title={t("schedule.exportSection")}>
                                    <IconButton
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleExportSection(section);
                                      }}
                                      size="small"
                                      sx={{ mr: 1 }}
                                      disabled={isExporting}
                                    >
                                      <ShareIcon />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </AccordionSummary>
                              <AccordionDetails>
                                {(tefilot[section.sectionId] || []).map((tefilah, tefilahIndex) => (
                                  <Box 
                                    key={tefilahIndex}
                                    sx={{ 
                                      display: 'flex',
                                      flexWrap: 'wrap',
                                      gap: 2,
                                      mb: 2,
                                      pb: 2,
                                      borderBottom: '1px solid #eee'
                                    }}
                                  >
                                    <TextField
                                      label={t("schedule.tefilahName")}
                                      value={tefilah.name || ""}
                                      onChange={(e) => handleUpdateTefilah(section.sectionId, tefilahIndex, "name", e.target.value)}
                                      sx={{ flex: '1 1 200px' }}
                                    />

                                    <FormControl sx={{ flex: '1 1 200px' }}>
                                      <Select
                                        value={tefilah.roomId || ""}
                                        onChange={(e) => handleUpdateTefilah(section.sectionId, tefilahIndex, "roomId", e.target.value)}
                                        displayEmpty
                                      >
                                        <MenuItem value="">{t('schedule.selectRoom')}</MenuItem>
                                        {rooms.map((room) => (
                                          <MenuItem key={room.roomId} value={room.roomId}>
                                            {room.roomName}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>

                                    <FormControl sx={{ flex: '1 1 200px' }}>
                                      <Select
                                        value={Boolean(tefilah.isVariable)}
                                        onChange={(e) => handleUpdateTefilah(section.sectionId, tefilahIndex, "isVariable", e.target.value)}
                                        displayEmpty
                                      >
                                        <MenuItem value="">{t('schedule.timeType')}</MenuItem>
                                        <MenuItem value={false}>{t('schedule.fixedTime')}</MenuItem>
                                        <MenuItem value={true}>{t('schedule.variableTime')}</MenuItem>
                                      </Select>
                                    </FormControl>

                                    {!Boolean(tefilah.isVariable) ? (
                                      <TextField
                                        type="time"
                                        value={tefilah.time || ""}
                                        onChange={(e) => handleUpdateTefilah(section.sectionId, tefilahIndex, "time", e.target.value)}
                                        sx={{ flex: '1 1 200px' }}
                                      />
                                    ) : (
                                      <>
                                        <FormControl sx={{ flex: '1 1 200px' }}>
                                          <Select
                                            value={tefilah.zman || ""}
                                            onChange={(e) => handleUpdateTefilah(section.sectionId, tefilahIndex, "zman", e.target.value)}
                                          >
                                            <MenuItem value="">{t('schedule.selectZman')}</MenuItem>
                                            <MenuItem value="Netz">{t('schedule.zman.netz')}</MenuItem>
                                            <MenuItem value="Chatsoth">{t('schedule.zman.chatsoth')}</MenuItem>
                                            <MenuItem value="MinhaGuedola">{t('schedule.zman.minhaGuedola')}</MenuItem>
                                            <MenuItem value="MinhaKetana">{t('schedule.zman.minhaKetana')}</MenuItem>
                                            <MenuItem value="PlagHaMincha">{t('schedule.zman.plagHaMincha')}</MenuItem>
                                            <MenuItem value="CandleLighting">{t('schedule.zman.candleLighting')}</MenuItem>
                                            <MenuItem value="Shekia">{t('schedule.zman.shekia')}</MenuItem>
                                            <MenuItem value="Stars">{t('schedule.zman.stars')}</MenuItem>
                                            <MenuItem value="Havdalah">{t('schedule.zman.havdalah')}</MenuItem>
                                          </Select>
                                        </FormControl>

                                        <TextField
                                          type="number"
                                          label={t('schedule.offset')}
                                          value={tefilah.offSetValue || 0}
                                          onChange={(e) => handleUpdateTefilah(section.sectionId, tefilahIndex, "offSetValue", e.target.value)}
                                          sx={{ flex: '1 1 200px' }}
                                        />
                                      </>
                                    )}

                                    <TextField
                                      type="date"
                                      label={t('schedule.startDate')}
                                      value={tefilah.startDate || ""}
                                      onChange={(e) => handleUpdateTefilah(section.sectionId, tefilahIndex, "startDate", e.target.value)}
                                      InputLabelProps={{ shrink: true }}
                                      sx={{ flex: '1 1 200px' }}
                                    />

                                    <TextField
                                      type="date"
                                      label={t('schedule.endDate')}
                                      value={tefilah.endDate || ""}
                                      onChange={(e) => handleUpdateTefilah(section.sectionId, tefilahIndex, "endDate", e.target.value)}
                                      InputLabelProps={{ shrink: true }}
                                      sx={{ flex: '1 1 200px' }}
                                    />

                                    <IconButton
                                      onClick={() => handleDeleteTefilah(section.sectionId, tefilahIndex)}
                                      color="error"
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </Box>
                                ))}
                                <Button
                                  startIcon={<AddIcon />}
                                  onClick={() => handleAddTefilah(section.sectionId)}
                                >
                                  {t("schedule.addTefilah")}
                                </Button>
                              </AccordionDetails>
                            </Accordion>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>

            {/* Dialog pour ajouter une nouvelle section */}
            <Dialog open={newSectionDialog} onClose={() => setNewSectionDialog(false)}>
              <DialogTitle>{t("schedule.newSection")}</DialogTitle>
              <DialogContent>
                <TextField
                  autoFocus
                  margin="dense"
                  label={t("schedule.sectionName")}
                  fullWidth
                  value={newSectionName}
                  onChange={(e) => setNewSectionName(e.target.value)}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setNewSectionDialog(false)}>
                  {t("common.cancel")}
                </Button>
                <Button onClick={handleAddSection} color="primary">
                  {t("common.add")}
                </Button>
              </DialogActions>
            </Dialog>
          </AccordionDetails>
        </Accordion>

        {/* Second Accordion pour les informations */}
        <Accordion
          expanded={isInformationAccordionOpen}
          onChange={(_, expanded) => setIsInformationAccordionOpen(expanded)}
          sx={{ mt: 2 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">{t('schedule.informationSection')}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {isLoadingInfo ? (
              <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <Suspense fallback={<CircularProgress />}>
                <TinyEditor
                  apiKey={process.env.REACT_APP_TINYMCE_API_KEY}
                  value={information}
                  onEditorChange={(content) => {
                    setInformation(content);
                    setIsDirty(true);
                  }}
                  init={{
                    height: 500,
                    menubar: false,
                    directionality: 'auto',
                    plugins: [
                      'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                      'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                      'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount',
                      'directionality'
                    ],
                    toolbar: 'undo redo | blocks | ' +
                      'bold italic forecolor | alignleft aligncenter ' +
                      'alignright alignjustify | bullist numlist outdent indent | ' +
                      'ltr rtl | removeformat | help',
                    content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
                    language: i18n.language === 'he' ? 'he_IL' : 'fr_FR',
                    // Ajout des traductions pour l'interface de l'éditeur
                    language_url: i18n.language === 'he' ? 
                      'https://cdn.tiny.cloud/1/no-api-key/tinymce/6/langs/he_IL.js' : 
                      'https://cdn.tiny.cloud/1/no-api-key/tinymce/6/langs/fr_FR.js'
                  }}
                />
              </Suspense>
            )}
          </AccordionDetails>
        </Accordion>
      </Box>

      <Box sx={{ mt: 2, display: "flex", justifyContent: "space-between" }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={!isDirty}
        >
          {t('schedule.save')}
        </Button>

        {/* <Button
          variant="contained"
          color="success"
          onClick={handleShareWhatsApp}
          startIcon={<WhatsAppIcon />}
        >
          {t('schedule.shareWhatsApp')}
        </Button> */}
      </Box>
      
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage('')}
        message={successMessage}
      />
      <Snackbar
        open={!!warning}
        autoHideDuration={3000}
        onClose={() => setWarning('')}
        message={warning}
      />
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={3000}
        onClose={() => setErrorMessage('')}
        message={errorMessage}
        severity="error"
      />

      {/* Dialog de confirmation de suppression */}
      <ConfirmDialog
        open={deleteConfirmDialog.open}
        title={t("schedule.confirmDelete")}
        message={t("schedule.confirmDeleteMessage")}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteConfirmDialog({ open: false, sectionId: null })}
        confirmText={t("common.delete")}
        cancelText={t("common.cancel")}
        severity="error"
      />

      {/* Composant d'export d'image - masqué en permanence */}
      <Box sx={{ position: 'absolute', left: '-9999px', top: '-9999px', visibility: 'hidden' }}>
        {exportingSection && (
          <SectionExportImage
            sectionName={exportingSection.sectionName}
            scheduleItems={exportingSection.processedData}
            onImageGenerated={handleImageGenerated}
          />
        )}
      </Box>
    </Box>
  );
};

export default ChaguimEditor;
